import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, Share2, Award, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_590a4cbe-6d2f-42fa-9080-72b33b8524af/artifacts/218oq24l_TCM-Logo.PNG";

export default function CertificatePage() {
  const { id } = useParams();
  const [contribution, setContribution] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContribution = async () => {
      try {
        const response = await axios.get(`${API}/contributions/${id}`);
        setContribution(response.data);
      } catch (error) {
        console.error("Error fetching contribution:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContribution();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: "My Carbon Contribution Certificate",
        text: `I contributed to climate action through The Carbon Meridian and offset ${contribution?.co2_offset} kg of CO₂!`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest-700" />
      </div>
    );
  }

  if (!contribution) {
    return (
      <div className="min-h-screen pt-20 bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Certificate not found</h1>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-slate-50" data-testid="certificate-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Actions - Hide on print */}
        <div className="no-print flex justify-center gap-4 mb-8">
          <Button onClick={handlePrint} className="bg-forest-950 hover:bg-forest-900" data-testid="download-certificate">
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
          <Button variant="outline" onClick={handleShare} data-testid="share-certificate">
            <Share2 className="mr-2 h-4 w-4" />
            Share
          </Button>
        </div>

        {/* Certificate */}
        <div className="certificate-container bg-white rounded-xl p-8 lg:p-12 shadow-lg">
          {/* Header */}
          <div className="text-center border-b border-slate-200 pb-6 mb-8">
            <img src={LOGO_URL} alt="The Carbon Meridian" className="h-16 mx-auto mb-4" />
            <h1 className="font-montserrat text-2xl font-bold text-forest-950">
              Carbon Contribution Certificate
            </h1>
          </div>

          {/* Award Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-forest-100 rounded-full flex items-center justify-center">
              <Award className="h-10 w-10 text-forest-700" />
            </div>
          </div>

          {/* Certificate Content */}
          <div className="text-center space-y-4 mb-8">
            <p className="text-slate-600">This certifies that</p>
            <p className="font-montserrat text-3xl font-bold text-slate-900" data-testid="certificate-name">
              {contribution.name}
            </p>
            <p className="text-slate-600">
              has made a contribution of
            </p>
            <p className="text-4xl font-bold text-forest-950">
              ₹{contribution.amount.toLocaleString()}
            </p>
            <p className="text-slate-600">
              through the <span className="font-semibold">{contribution.program_name}</span> program
            </p>
          </div>

          {/* CO2 Offset */}
          <div className="bg-gradient-to-r from-forest-50 to-earth-50 rounded-xl p-6 text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Leaf className="h-5 w-5 text-forest-600" />
              <span className="text-sm text-forest-700 font-medium">Carbon Offset</span>
            </div>
            <p className="text-4xl font-bold text-forest-950" data-testid="certificate-co2">
              {contribution.co2_offset.toLocaleString()} kg CO₂
            </p>
            <p className="text-sm text-slate-600 mt-1">equivalent emissions offset</p>
          </div>

          {/* Certificate Details */}
          <div className="grid grid-cols-2 gap-4 text-sm border-t border-slate-200 pt-6">
            <div>
              <p className="text-slate-500">Certificate ID</p>
              <p className="font-mono font-medium text-slate-900" data-testid="certificate-id">
                {contribution.certificate_id}
              </p>
            </div>
            <div className="text-right">
              <p className="text-slate-500">Date</p>
              <p className="font-medium text-slate-900">
                {new Date(contribution.created_at).toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 pt-6 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              This certificate is issued by The Carbon Meridian and represents a verified contribution 
              towards carbon offset projects in India.
            </p>
            <p className="text-xs text-slate-400 mt-2">
              www.thecarbonmeridian.com
            </p>
          </div>
        </div>

        {/* Back Link - Hide on print */}
        <div className="no-print text-center mt-8">
          <Link to="/" className="text-forest-700 hover:text-forest-900 font-medium">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
